// ============================================
// DOCUMENT UPLOAD MODAL
// ============================================

import { useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, FileText, Loader2, Calendar, FolderOpen } from 'lucide-react';
import { useCreateDocument, useDocumentFolders } from '@/hooks';
import { commonApi } from '@crew-at-sea/api-client';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

interface DocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// Belge türü key'leri — çeviri panel:documents.types.* ile yapılıyor
const DOCUMENT_TYPE_KEYS = [
  'PASSPORT', 'CDC', 'CERTIFICATE', 'DIPLOMA',
  'MEDICAL', 'FLAG_DOCUMENT', 'CONTRACT', 'OTHER',
] as const;

export function DocumentUploadModal({ open, onOpenChange, onSuccess }: DocumentUploadModalProps) {
  const { t } = useTranslation(['panel', 'common']);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('OTHER');
  const [folderId, setFolderId] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: foldersResponse } = useDocumentFolders();
  const createDocumentMutation = useCreateDocument();

  const folders: any[] = (foldersResponse as any)?.data || foldersResponse || [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Dosya adından otomatik doldur
      if (!name) {
        setName(selectedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!name) {
        setName(droppedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const resetForm = () => {
    setFile(null);
    setName('');
    setType('OTHER');
    setFolderId('');
    setExpiryDate('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError(t('panel:documents.selectFile'));
      return;
    }

    if (!name.trim()) {
      setError(t('panel:documents.documentNameRequired'));
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Önce dosyayı API'ye yükle
      const uploadResult = await commonApi.uploadDocument(file);
      const fileUrl = uploadResult?.data?.url || uploadResult?.url;

      if (!fileUrl) {
        throw new Error(t('panel:documents.uploadFailed'));
      }

      // Sonra belge kaydını oluştur
      await createDocumentMutation.mutateAsync({
        name: name.trim(),
        type: type,
        fileUrl: fileUrl,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        folderId: folderId || undefined,
        expiryDate: expiryDate ? new Date(expiryDate).toISOString() : undefined,
      });

      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Document upload error:', err);
      if (err?.message?.includes('too large') || err?.status === 413) {
        setError(t('panel:documents.fileTooLarge'));
      } else {
        setError(err?.message || t('panel:documents.uploadError'));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-lg z-[100] max-h-[90dvh] sm:max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {t('panel:documents.uploadTitle')}
            </Dialog.Title>
            <Dialog.Close className="p-1 rounded-maritime hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            {t('panel:documents.uploadDescription')}
          </Dialog.Description>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-maritime text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Dosya Sürükle/Bırak Alanı */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={cn(
                'border-2 border-dashed rounded-maritime p-6 text-center cursor-pointer transition-colors',
                file ? 'border-maritime-500 bg-maritime-50' : 'border-gray-300 hover:border-maritime-400 hover:bg-gray-50'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-maritime-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {t('panel:documents.dragOrSelect').split('<1>')[0]}
                    <span className="text-maritime-600 font-medium">{t('panel:documents.dragOrSelectText')}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('panel:documents.fileFormats')}
                  </p>
                </>
              )}
            </div>

            {/* Belge Adı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:documents.documentName')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input w-full"
                placeholder={t('panel:documents.documentNamePlaceholder')}
                required
              />
            </div>

            {/* Belge Türü */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('panel:documents.documentType')} *
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="input w-full"
                required
              >
                {DOCUMENT_TYPE_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`panel:documents.types.${key}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Klasör */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FolderOpen className="w-4 h-4 inline mr-1" />
                {t('panel:documents.folder')}
              </label>
              <select
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                className="input w-full"
              >
                <option value="">{t('panel:documents.selectFolder')}</option>
                {folders.map((folder: any) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Geçerlilik Tarihi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('panel:documents.expiryDateOptional')}
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="input w-full"
              />
            </div>

            {/* Aksiyonlar */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
                className="btn-secondary"
                disabled={uploading}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={uploading || !file}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('panel:documents.uploading')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {t('common:upload')}
                  </>
                )}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
